import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, type DragEndEvent, type DraggableAttributes, type DraggableSyntheticListeners,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Calendar, CalendarClock, Check, ChevronDown, ChevronRight, Clock, Copy, Ellipsis, Flag, FolderInput, GripVertical, ListTree, MessageSquare, Plus, Repeat, RotateCcw, Trash2,
} from "lucide-react";
import { createContext, memo, useContext, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { colorHex, toISODate, type Due, type Priority, type Task } from "@todoer/shared";
import { db, useCollaborators } from "../lib/db.js";
import { dueLabel, fmtDuration, shortDate } from "../lib/format.js";
import { selectRange, toggleSelected, useSelection, visibleTaskIds } from "../lib/selection.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";
import { DuePopover } from "./DuePicker.js";
import { MenuList, Popover } from "./overlay.js";
import { PriorityList, priorityMeta, ProjectList } from "./pickers.js";
import { QuickAdd } from "./QuickAdd.js";
import { SwipeRow } from "./Swipe.js";
import { Avatar, cx, EmptyState, IconButton, useIsPhone } from "./ui.js";
import type { IllustrationKind } from "./Illustration.js";

export { dueLabel, shortDate };

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

/** Completes or reopens a task, with an Undo toast on completion. */
export async function toggleTask(task: Task): Promise<void> {
  if (task.completedAt) {
    await dispatch(cmd("item_uncomplete", { id: task.id }));
    showToast(`Reopened "${task.content}"`);
    return;
  }
  const prevDue = task.due;
  await dispatch(cmd("item_complete", { id: task.id }));
  showToast(prevDue?.isRecurring ? `Completed "${task.content}", next one scheduled` : `Completed "${task.content}"`, {
    label: "Undo",
    run: () => prevDue?.isRecurring
      ? dispatch(cmd("item_update", { id: task.id, due: prevDue }))
      : dispatch(cmd("item_uncomplete", { id: task.id })),
  });
}

export async function deleteTask(task: Pick<Task, "id" | "content">): Promise<void> {
  await dispatch(cmd("item_delete", { id: task.id }));
  showToast(`Deleted "${task.content}"`, { label: "Undo", run: () => dispatch(cmd("item_restore", { id: task.id })) });
}

export async function duplicateTask(task: Task): Promise<void> {
  await dispatch(cmd("item_add", {
    id: uuid(), projectId: task.projectId, sectionId: task.sectionId, parentId: task.parentId, content: task.content, description: task.description,
    priority: task.priority, due: task.due, deadline: task.deadline, durationMinutes: task.durationMinutes, labelIds: task.labelIds, childOrder: task.childOrder,
    assigneeId: task.assigneeId,
  }));
  showToast(`Duplicated "${task.content}"`);
}

export const openTaskParams = (id: string, extra?: Record<string, string>) => (p: URLSearchParams) => {
  p.set("task", id);
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  return p;
};

// ------------------------------------------------------------------ checkbox
/** 20px priority-coloured circle. Plays a fill + check animation before calling onToggle. */
export function TaskCheckbox({ priority, done, onToggle, checking, size = 20, label }: { priority: Priority; done: boolean; onToggle: () => void; checking?: boolean; size?: number; label?: string }) {
  const color = priority > 1 ? priorityMeta(priority).color : "var(--color-border-strong)";
  const filled = done || checking;
  const fill = priority > 1 ? color : "var(--color-text-faint)";
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} aria-label={label ?? (done ? "Reopen task" : "Complete task")} aria-pressed={done}
      data-swipe-ignore className="group/check -m-3 grid shrink-0 place-items-center p-3 phone:-m-3 phone:p-3">
      <span className={cx("relative grid place-items-center rounded-full border-[1.5px] transition-colors duration-150", checking && "anim-fill")}
        style={{ width: size, height: size, borderColor: filled ? fill : color, background: filled ? fill : priority > 1 ? `color-mix(in srgb, ${color} 10%, transparent)` : undefined, ["--fill" as string]: fill }}>
        <Check size={size * 0.62} strokeWidth={3} aria-hidden
          className={cx("transition-opacity duration-150", filled ? "text-white opacity-100" : "opacity-0 group-hover/check:opacity-60", checking && "anim-check")}
          style={filled ? undefined : { color }} />
      </span>
    </button>
  );
}

// ------------------------------------------------------------------ row
export interface RowDnd {
  ref?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  attributes?: DraggableAttributes;
  listeners?: DraggableSyntheticListeners;
  dragging?: boolean;
  /** Show a touch drag handle (phone) */
  handle?: boolean;
}

interface RowProps {
  task: Task;
  showProject?: boolean;
  showCompletedAt?: boolean;
  depth?: number;
  childCount?: { done: number; total: number };
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  dnd?: RowDnd;
  /** Collapse the row out of the list after completing (lists that hide completed tasks) */
  hideOnComplete?: boolean;
  /** Compact rows inside the task panel */
  dense?: boolean;
}

type Pop = { kind: "date" | "menu" | "move" | "priority"; anchor: HTMLElement } | null;

export const TaskRow = memo(function TaskRow({ task, showProject = false, showCompletedAt = false, depth = 0, childCount, collapsed, onToggleCollapse, dnd, hideOnComplete = true, dense }: RowProps) {
  const [, setParams] = useSearchParams();
  const sel = useSelection();
  const selected = sel.ids.includes(task.id);
  const selecting = sel.ids.length > 0;
  const people = useCollaborators();
  const isPhone = useIsPhone();
  const [phase, setPhase] = useState<"idle" | "checking" | "collapsing">("idle");
  const [pop, setPop] = useState<Pop>(null);
  const rowRef = useRef<HTMLLIElement | null>(null);
  const extras = useLiveQuery(async () => {
    const [project, labels, comments, kids] = await Promise.all([
      showProject ? db.projects.get(task.projectId) : undefined,
      task.labelIds.length ? db.labels.where("id").anyOf(task.labelIds).toArray() : [],
      db.comments.where("taskId").equals(task.id).count(),
      childCount ? null : db.tasks.where("parentId").equals(task.id).toArray(),
    ]);
    return { project, labels, comments, kids };
  }, [task.projectId, showProject, task.labelIds.join(","), task.id, task.updatedAt, !!childCount]);
  const counts = childCount ?? (extras?.kids?.length ? { done: extras.kids.filter((k) => k.completedAt).length, total: extras.kids.length } : undefined);
  const done = !!task.completedAt;
  const due = done ? null : dueLabel(task);
  const today = toISODate(new Date());
  const firstLine = task.description.split("\n").find((l) => l.trim());
  const assignee = task.assigneeId ? people.find((p) => p.id === task.assigneeId) : undefined;

  const open = () => setParams(openTaskParams(task.id));
  const complete = () => {
    if (phase !== "idle") return;
    if (done) { void toggleTask(task); return; }
    const collapse = hideOnComplete && !task.due?.isRecurring;
    const quick = reducedMotion();
    setPhase("checking");
    window.setTimeout(() => { if (collapse) setPhase("collapsing"); }, quick ? 120 : 260);
    window.setTimeout(() => { void toggleTask(task).finally(() => { if (!collapse) setPhase("idle"); }); }, collapse ? (quick ? 300 : 480) : 300);
  };
  const setPriority = (p: Priority) => void dispatch(cmd("item_update", { id: task.id, priority: p }));

  const onClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button, a, input")) return;
    if (e.shiftKey) { e.preventDefault(); selectRange(task.id, visibleTaskIds()); return; }
    if (e.metaKey || e.ctrlKey || selecting) { e.preventDefault(); toggleSelected(task.id); return; }
    open();
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget || e.metaKey || e.ctrlKey || e.altKey) { dnd?.listeners?.onKeyDown?.(e); return; }
    const k = e.key.toLowerCase();
    if (e.key === "Enter") { e.preventDefault(); open(); return; }
    if (k === "e") { e.preventDefault(); complete(); return; }
    if (k === "d") { e.preventDefault(); setPop({ kind: "date", anchor: e.currentTarget }); return; }
    if (["1", "2", "3", "4"].includes(e.key)) { e.preventDefault(); setPriority((5 - Number(e.key)) as Priority); return; }
    if (k === "x") { e.preventDefault(); toggleSelected(task.id); return; }
    dnd?.listeners?.onKeyDown?.(e);
  };

  const menuItems = [
    { label: "Schedule…", icon: Calendar, shortcut: "D", onSelect: () => rowRef.current && setPop({ kind: "date", anchor: pop?.anchor ?? rowRef.current }) },
    { label: "Move to…", icon: FolderInput, onSelect: () => setPop((p) => ({ kind: "move", anchor: p?.anchor ?? rowRef.current! })) },
    { label: "Priority…", icon: Flag, onSelect: () => setPop((p) => ({ kind: "priority", anchor: p?.anchor ?? rowRef.current! })) },
    { label: "Duplicate", icon: Copy, onSelect: () => void duplicateTask(task) },
    { label: "Delete", icon: Trash2, danger: true, divider: true, onSelect: () => void deleteTask(task) },
  ];

  const indent = depth * (isPhone ? 20 : 28);
  const toggleChevron = onToggleCollapse && counts && counts.total > 0;

  return (
    <li ref={(el) => { rowRef.current = el; dnd?.ref?.(el); }} style={dnd?.style} data-task-id={task.id} tabIndex={0}
      aria-selected={selecting ? selected : undefined} aria-describedby={dnd?.attributes?.["aria-describedby"]} aria-roledescription={dnd ? "sortable task" : undefined}
      onPointerDown={dnd?.listeners?.onPointerDown as React.PointerEventHandler | undefined} onKeyDown={onKeyDown}
      data-collapsing={phase === "collapsing"}
      className={cx("row-collapse group/row relative list-none rounded-control outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus", dnd?.dragging && "z-20")}>
      <div>
        <SwipeRow enabled={isPhone && !done && !dnd?.dragging}
          right={{ key: "done", label: "Complete", icon: Check, tone: "success", onSelect: complete }}
          left={[
            { key: "date", label: "Schedule", icon: CalendarClock, tone: "warning", onSelect: () => rowRef.current && setPop({ kind: "date", anchor: rowRef.current }) },
            { key: "delete", label: "Delete", icon: Trash2, tone: "danger", onSelect: () => void deleteTask(task) },
          ]}
          fullLeft={() => void deleteTask(task)}
          onLongPress={() => toggleSelected(task.id)}
          contentClassName={cx(dnd?.dragging && "rounded-card bg-surface-raised shadow-pop")}>
          <div onClick={onClick}
            className={cx("relative flex cursor-pointer items-start gap-3 border-b border-border pr-1 transition-colors duration-150",
              dense ? "py-2" : "py-2.5 phone:min-h-[52px] phone:py-3",
              selected ? "bg-brand-soft shadow-[inset_2px_0_0_var(--color-brand)]" : "desk:hover:bg-hover",
              dnd?.dragging && "border-transparent")}
            style={{ paddingLeft: indent + (selecting ? 28 : 4) }}>
            {selecting && (
              <button type="button" role="checkbox" aria-checked={selected} aria-label={selected ? "Deselect task" : "Select task"} data-swipe-ignore
                onClick={(e) => { e.stopPropagation(); toggleSelected(task.id); }}
                className={cx("absolute top-1/2 grid h-4 w-4 -translate-y-1/2 place-items-center rounded-[4px] border transition-opacity", selected ? "border-brand bg-brand-strong text-white" : "border-border-strong bg-surface-raised opacity-0 group-hover/row:opacity-100 phone:opacity-100")}
                style={{ left: indent + 6 }}>
                {selected && <Check size={12} strokeWidth={3} aria-hidden />}
              </button>
            )}
            {dnd?.handle && !isPhone && !selecting && (
              <span aria-hidden data-tip="Drag to reorder" className="absolute top-1/2 grid h-7 w-5 -translate-y-1/2 cursor-grab place-items-center text-text-faint opacity-0 transition-opacity group-hover/row:opacity-100 active:cursor-grabbing"
                style={{ left: indent - 22 }}><GripVertical size={14} /></span>
            )}
            <div className="pt-0.5">
              <TaskCheckbox priority={task.priority} done={done} checking={phase !== "idle"} onToggle={complete} />
            </div>
            <div className="min-w-0 flex-1">
              <div className={cx("break-words text-body", done && "text-text-faint line-through decoration-text-faint/60")}>{task.content}</div>
              {firstLine && <div className="truncate text-meta text-text-faint">{firstLine}</div>}
              <TaskMeta task={task} due={due} today={today} counts={counts} comments={extras?.comments ?? 0} labels={extras?.labels ?? []}
                collapsed={collapsed} onToggleCollapse={toggleChevron ? onToggleCollapse : undefined}
                completedAt={showCompletedAt ? task.completedAt : null} />
            </div>
            <div className="flex shrink-0 items-center gap-2 self-center">
              {showProject && extras?.project && (
                <span className="flex max-w-36 items-center gap-1.5 text-meta text-text-faint phone:max-w-24">
                  <span className="truncate">{extras.project.isInbox ? "Inbox" : extras.project.name}</span>
                  <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: extras.project.isInbox ? "var(--color-info)" : colorHex(extras.project.color) }} />
                </span>
              )}
              {assignee && <Avatar person={assignee} size={22} />}
              {done && <button type="button" data-swipe-ignore onClick={(e) => { e.stopPropagation(); void toggleTask(task); }}
                className="inline-flex h-7 items-center gap-1 rounded-control px-2 text-meta font-medium text-text-muted hover:bg-hover hover:text-text phone:h-10"><RotateCcw size={13} aria-hidden />Reopen</button>}
              {dnd?.handle && isPhone && (
                <span data-swipe-ignore onTouchStart={dnd.listeners?.onTouchStart as React.TouchEventHandler | undefined} aria-hidden className="-my-2 -mr-1 grid h-11 w-8 touch-none place-items-center text-text-faint">
                  <GripVertical size={16} />
                </span>
              )}
            </div>
            {!done && !isPhone && (
              <div className={cx("absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-control bg-surface-raised p-0.5 opacity-0 shadow-[0_0_0_1px_var(--color-border)] transition-opacity duration-150 group-hover/row:opacity-100 group-focus-within/row:opacity-100", (pop || selecting) && "opacity-100", selecting && !pop && "hidden")}>
                <IconButton size="sm" icon={Calendar} label="Schedule" shortcut="D" onClick={(e) => setPop({ kind: "date", anchor: e.currentTarget })} />
                <IconButton size="sm" icon={MessageSquare} label="Comment" onClick={() => setParams(openTaskParams(task.id, { focus: "comment" }))} />
                <IconButton size="sm" icon={Ellipsis} label="More actions" onClick={(e) => setPop({ kind: "menu", anchor: e.currentTarget })} active={pop?.kind === "menu"} />
              </div>
            )}
          </div>
        </SwipeRow>
      </div>
      {pop?.kind === "date" && <DuePopover anchor={pop.anchor} value={task.due} onClose={() => setPop(null)} onChange={(d) => void dispatch(cmd("item_update", { id: task.id, due: d }))} />}
      {pop?.kind === "menu" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title="Task actions" align="end" width={220}>
          <MenuList label="Task actions" items={menuItems} onClose={() => { /* the chosen item decides */ }} />
        </Popover>
      )}
      {pop?.kind === "move" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title="Move to" align="end" width={260}>
          <ProjectList withSections value={task.projectId} sectionValue={task.sectionId} onSelect={(projectId, sectionId) => {
            setPop(null);
            if (projectId) void dispatch(cmd("item_move", { id: task.id, projectId, sectionId, parentId: null }));
          }} />
        </Popover>
      )}
      {pop?.kind === "priority" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title="Priority" align="end" width={200}>
          <PriorityList value={task.priority} onSelect={(p) => { setPop(null); setPriority(p); }} />
        </Popover>
      )}
    </li>
  );
});

function TaskMeta({ task, due, today, counts, comments, labels, collapsed, onToggleCollapse, completedAt }: {
  task: Task; due: ReturnType<typeof dueLabel>; today: string; counts?: { done: number; total: number }; comments: number;
  labels: { id: string; name: string }[]; collapsed?: boolean; onToggleCollapse?: () => void; completedAt: string | null;
}) {
  const items: ReactNode[] = [];
  if (due) {
    const Icon = task.due?.isRecurring ? Repeat : Calendar;
    items.push(<span key="due" className={cx("inline-flex items-center gap-1", due.cls)}><Icon size={12} aria-hidden />{due.text}</span>);
  }
  if (task.deadline && !task.completedAt) {
    items.push(<span key="dl" className={cx("inline-flex items-center gap-1", task.deadline < today ? "text-danger" : "text-text-muted")} data-tip="Deadline"><Flag size={12} aria-hidden />{shortDate(task.deadline)}</span>);
  }
  if (task.durationMinutes) items.push(<span key="dur" className="inline-flex items-center gap-1"><Clock size={12} aria-hidden />{fmtDuration(task.durationMinutes)}</span>);
  if (counts && counts.total > 0) {
    const label = `${counts.done}/${counts.total}`;
    items.push(onToggleCollapse ? (
      <button key="sub" type="button" data-swipe-ignore onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }} aria-expanded={!collapsed}
        aria-label={`${collapsed ? "Show" : "Hide"} sub-tasks (${label})`} className="-my-1 inline-flex items-center gap-1 rounded px-1 py-1 hover:bg-hover hover:text-text phone:py-2">
        <ListTree size={12} aria-hidden />{label}
        <span className="opacity-0 transition-opacity group-hover/row:opacity-100 phone:opacity-100">{collapsed ? <ChevronRight size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />}</span>
      </button>
    ) : <span key="sub" className="inline-flex items-center gap-1" data-tip="Sub-tasks"><ListTree size={12} aria-hidden />{label}</span>);
  }
  if (comments) items.push(<span key="c" className="inline-flex items-center gap-1" data-tip="Comments"><MessageSquare size={12} aria-hidden />{comments}</span>);
  for (const l of labels) items.push(<span key={l.id} className="text-text-muted">@{l.name}</span>);
  if (completedAt) items.push(<span key="done" className="inline-flex items-center gap-1"><Check size={12} aria-hidden />{new Date(completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>);
  if (!items.length) return null;
  return <div className="tabular mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-meta text-text-faint">{items}</div>;
}

// ------------------------------------------------------------------ lists
/** The main scroll container, for virtualised lists. */
export const ScrollParent = createContext<() => HTMLElement | null>(() => (typeof document === "undefined" ? null : document.getElementById("main-scroll")));

/** Flat list (date-based views, filters, search). Virtualised over 200 rows. */
export function TaskList({ tasks, showProject, emptyText, emptyKind = "tasks", emptyTitle, projectId, sectionId, defaultDue, showCompletedAt, hideOnComplete, addButton = true }: {
  tasks: Task[]; showProject?: boolean; emptyText?: ReactNode; emptyKind?: IllustrationKind; emptyTitle?: string; projectId?: string | null; sectionId?: string | null; defaultDue?: Due | null;
  showCompletedAt?: boolean; hideOnComplete?: boolean; addButton?: boolean;
}) {
  return (
    <div>
      {tasks.length > 200
        ? <VirtualTaskList tasks={tasks} showProject={showProject} showCompletedAt={showCompletedAt} hideOnComplete={hideOnComplete} />
        : <ul>{tasks.map((t) => <TaskRow key={t.id} task={t} showProject={showProject} showCompletedAt={showCompletedAt} hideOnComplete={hideOnComplete} />)}</ul>}
      {addButton && <AddTaskButton projectId={projectId} sectionId={sectionId} defaultDue={defaultDue} />}
      {tasks.length === 0 && emptyTitle && <EmptyState compact kind={emptyKind} title={emptyTitle}>{emptyText}</EmptyState>}
    </div>
  );
}

export function VirtualTaskList({ tasks, showProject, showCompletedAt, hideOnComplete }: { tasks: Task[]; showProject?: boolean; showCompletedAt?: boolean; hideOnComplete?: boolean }) {
  const getScroll = useContext(ScrollParent);
  const listRef = useRef<HTMLUListElement>(null);
  const [margin, setMargin] = useState(0);
  useLayoutEffect(() => {
    const el = listRef.current;
    const scroller = getScroll();
    if (el && scroller) setMargin(el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop);
  }, [getScroll]);
  const v = useVirtualizer({ count: tasks.length, getScrollElement: getScroll, estimateSize: () => 56, overscan: 12, scrollMargin: margin });
  return (
    <ul ref={listRef} style={{ height: v.getTotalSize(), position: "relative" }}>
      {v.getVirtualItems().map((item) => {
        const t = tasks[item.index]!;
        return (
          <div key={t.id} data-index={item.index} ref={v.measureElement} style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${item.start - margin}px)` }}>
            <TaskRow task={t} showProject={showProject} showCompletedAt={showCompletedAt} hideOnComplete={hideOnComplete} />
          </div>
        );
      })}
    </ul>
  );
}

export function AddTaskButton({ projectId, sectionId, defaultDue, parentId, label }: { projectId?: string | null; sectionId?: string | null; defaultDue?: Due | null; parentId?: string | null; label?: string }) {
  const [adding, setAdding] = useState(false);
  return adding ? (
    <div className="mt-2"><QuickAdd inline projectId={projectId} sectionId={sectionId} parentId={parentId} defaultDue={defaultDue} onClose={() => setAdding(false)} /></div>
  ) : (
    <button type="button" onClick={() => setAdding(true)}
      className="group/add flex h-10 w-full items-center gap-3 rounded-control px-1 text-body text-text-faint transition-colors hover:text-brand-text phone:h-12">
      <span className="grid h-5 w-5 place-items-center rounded-full text-brand transition-colors group-hover/add:bg-brand-strong group-hover/add:text-white"><Plus size={16} aria-hidden /></span>
      {label ?? (parentId ? "Add sub-task" : "Add task")}
    </button>
  );
}

// ------------------------------------------------------------------ sortable tree
export interface FlatItem { task: Task; depth: number; childCount: { done: number; total: number } }

export function subtaskCounts(all: Task[]): Map<string, { done: number; total: number }> {
  const counts = new Map<string, { done: number; total: number }>();
  for (const t of all) {
    if (!t.parentId) continue;
    const c = counts.get(t.parentId) ?? { done: 0, total: 0 };
    c.total++; if (t.completedAt) c.done++;
    counts.set(t.parentId, c);
  }
  return counts;
}

export function flatten(tasks: Task[], allForCounts: Task[]): FlatItem[] {
  const ids = new Set(tasks.map((t) => t.id));
  const kids = new Map<string | null, Task[]>();
  for (const t of tasks) {
    const key = t.parentId && ids.has(t.parentId) ? t.parentId : null;
    kids.set(key, [...(kids.get(key) ?? []), t]);
  }
  const counts = subtaskCounts(allForCounts);
  const out: FlatItem[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const t of (kids.get(parent) ?? []).sort((a, b) => a.childOrder - b.childOrder || a.updatedAt.localeCompare(b.updatedAt))) {
      out.push({ task: t, depth, childCount: counts.get(t.id) ?? { done: 0, total: 0 } });
      if (!t.isCollapsed) walk(t.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function SortableRow({ item, dense }: { item: FlatItem; dense?: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.task.id, data: { type: "task", sectionId: item.task.sectionId } });
  return (
    <TaskRow task={item.task} depth={item.depth} childCount={item.childCount} dense={dense}
      collapsed={item.task.isCollapsed}
      onToggleCollapse={() => void dispatch(cmd("item_update", { id: item.task.id, isCollapsed: !item.task.isCollapsed }))}
      dnd={{ ref: setNodeRef, style: { transform: CSS.Translate.toString(transform), transition }, attributes, listeners, dragging: isDragging, handle: true }} />
  );
}

/** Pointer sensor for mouse and pen only; touch drags start from the row's grip handle. */
class MouseSensor extends PointerSensor {
  static activators = [{
    eventName: "onPointerDown" as const,
    handler: ({ nativeEvent: e }: React.PointerEvent) =>
      e.pointerType !== "touch" && e.isPrimary && e.button === 0 && !(e.target as HTMLElement).closest("button, a, input, textarea, [role=checkbox]"),
  }];
}

export function useTaskSensors() {
  return useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] } }),
  );
}

/** Reorders `active` next to `target` among the target's siblings. */
export function reorderCommands(tasks: Task[], flat: FlatItem[], activeId: string, overId: string, sectionId: string | null) {
  const active = tasks.find((t) => t.id === activeId);
  const target = tasks.find((t) => t.id === overId);
  if (!active || !target) return null;
  for (let p: Task | undefined = target; p; p = tasks.find((t) => t.id === p!.parentId)) if (p.id === activeId) return null;
  const fromIndex = flat.findIndex((f) => f.task.id === activeId);
  const toIndex = flat.findIndex((f) => f.task.id === overId);
  const siblings = tasks.filter((t) => t.parentId === target.parentId && (t.sectionId ?? null) === (target.sectionId ?? null) && t.id !== activeId).sort((a, b) => a.childOrder - b.childOrder);
  const at = siblings.findIndex((t) => t.id === overId) + (fromIndex >= 0 && fromIndex < toIndex ? 1 : 0);
  siblings.splice(at, 0, active);
  return cmd("item_reorder", {
    items: siblings.map((t, i) => ({ id: t.id, childOrder: i + 1, ...(t.id === activeId ? { parentId: target.parentId, sectionId } : {}) })),
  });
}

/** Tasks of one container (a sub-task list) with nesting and drag to reorder. */
export function TaskTree({ tasks, allTasks, projectId, sectionId, parentId = null, emptyText, dense }: {
  tasks: Task[]; allTasks: Task[]; projectId: string; sectionId: string | null; parentId?: string | null; emptyText?: string; dense?: boolean;
}) {
  const flat = useMemo(() => flatten(tasks, allTasks), [tasks, allTasks]);
  const sensors = useTaskSensors();
  const onDragEnd = (e: DragEndEvent) => {
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || overId === String(e.active.id)) return;
    const c = reorderCommands(tasks, flat, String(e.active.id), overId, sectionId);
    if (c) void dispatch(c);
  };
  return (
    <div>
      {flat.length === 0 && emptyText && <p className="py-3 text-body text-text-faint">{emptyText}</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={flat.map((f) => f.task.id)} strategy={verticalListSortingStrategy}>
          <ul>{flat.map((f) => <SortableRow key={f.task.id} item={f} dense={dense} />)}</ul>
        </SortableContext>
      </DndContext>
      <AddTaskButton projectId={projectId} sectionId={sectionId} parentId={parentId} />
    </div>
  );
}

/** Collapsible "Completed (n)" list for a project. */
export function CompletedInProject({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const done = useLiveQuery(() => db.tasks.where("projectId").equals(projectId).filter((t) => !!t.completedAt).toArray(), [projectId], []);
  if (!done.length) return null;
  const sorted = [...done].sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""));
  return (
    <section className="mt-8">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex h-9 items-center gap-1.5 rounded-control px-1 text-body font-medium text-text-muted hover:text-text">
        {open ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}Completed <span className="tabular text-text-faint">{done.length}</span>
      </button>
      {open && <ul className="mt-1">{sorted.map((t) => <TaskRow key={t.id} task={t} hideOnComplete={false} />)}</ul>}
    </section>
  );
}

/** Sort: incomplete first, then by date/time, priority, manual order. */
export function sortTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) =>
    Number(!!a.completedAt) - Number(!!b.completedAt) ||
    (a.due?.date ?? "9999").localeCompare(b.due?.date ?? "9999") ||
    (a.due?.datetime ?? "").localeCompare(b.due?.datetime ?? "") ||
    b.priority - a.priority ||
    a.childOrder - b.childOrder ||
    a.updatedAt.localeCompare(b.updatedAt));
}

// ------------------------------------------------------------------ card (board, matrix)
export function TaskCard({ task, counts, dnd, showProject }: { task: Task; counts?: { done: number; total: number }; dnd?: RowDnd; showProject?: boolean }) {
  const [, setParams] = useSearchParams();
  const people = useCollaborators();
  const sel = useSelection();
  const selected = sel.ids.includes(task.id);
  const [checking, setChecking] = useState(false);
  const extras = useLiveQuery(async () => ({
    labels: task.labelIds.length ? await db.labels.where("id").anyOf(task.labelIds).toArray() : [],
    comments: await db.comments.where("taskId").equals(task.id).count(),
    project: showProject ? await db.projects.get(task.projectId) : undefined,
  }), [task.id, task.labelIds.join(","), task.updatedAt, showProject]);
  const assignee = task.assigneeId ? people.find((p) => p.id === task.assigneeId) : undefined;
  const due = dueLabel(task);
  const complete = () => { setChecking(true); window.setTimeout(() => void toggleTask(task), reducedMotion() ? 100 : 320); };
  const onClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if (e.metaKey || e.ctrlKey || sel.ids.length) { toggleSelected(task.id); return; }
    setParams(openTaskParams(task.id));
  };
  return (
    <div ref={dnd?.ref} style={dnd?.style} data-task-id={task.id} {...(dnd?.attributes ?? {})} {...(dnd?.listeners ?? {})} tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) setParams(openTaskParams(task.id)); else dnd?.listeners?.onKeyDown?.(e); }}
      onClick={onClick}
      className={cx("group/card cursor-pointer rounded-card border bg-surface-raised p-3 text-left outline-none transition-[border-color,box-shadow,opacity] duration-150 focus-visible:ring-2 focus-visible:ring-focus",
        selected ? "border-brand bg-brand-soft" : "border-border hover:border-border-strong",
        dnd?.dragging && "opacity-90 shadow-pop", checking && "opacity-60")}>
      <div className="flex items-start gap-2.5">
        <div className="pt-0.5"><TaskCheckbox priority={task.priority} done={!!task.completedAt} checking={checking} onToggle={complete} size={18} /></div>
        <div className="min-w-0 flex-1 break-words text-body">{task.content}</div>
      </div>
      {(due || task.durationMinutes || (counts?.total ?? 0) > 0 || !!extras?.comments || !!extras?.labels.length || assignee || extras?.project) && (
        <div className="mt-2 flex items-end gap-2 pl-[28px]">
          <div className="tabular flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1 text-meta text-text-faint">
            {due && <span className={cx("inline-flex items-center gap-1", due.cls)}>{task.due?.isRecurring ? <Repeat size={12} aria-hidden /> : <Calendar size={12} aria-hidden />}{due.text}</span>}
            {task.durationMinutes && <span className="inline-flex items-center gap-1"><Clock size={12} aria-hidden />{fmtDuration(task.durationMinutes)}</span>}
            {counts && counts.total > 0 && <span className="inline-flex items-center gap-1"><ListTree size={12} aria-hidden />{counts.done}/{counts.total}</span>}
            {!!extras?.comments && <span className="inline-flex items-center gap-1"><MessageSquare size={12} aria-hidden />{extras.comments}</span>}
            {extras?.labels.map((l) => <span key={l.id} className="text-text-muted">@{l.name}</span>)}
            {extras?.project && <span className="inline-flex items-center gap-1"><span aria-hidden className="h-2 w-2 rounded-full" style={{ background: extras.project.isInbox ? "var(--color-info)" : colorHex(extras.project.color) }} />{extras.project.isInbox ? "Inbox" : extras.project.name}</span>}
          </div>
          {assignee && <Avatar person={assignee} size={22} />}
        </div>
      )}
    </div>
  );
}
